/**
 * TrendingStartups — "Trending Startups & Business Models" from our 4,613-company VC corpus.
 * Honest "rising" = founding momentum (where VC-backed founders are concentrating now).
 * Categories + named business-model clusters (e.g. "like Cursor") + newest funded companies.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Fire, TrendUp, Minus, Snowflake, Sparkle, ArrowRight, ArrowUpRight,
  Buildings, Trophy, Globe, X,
} from "@phosphor-icons/react";
import { BarChart, Bar, ResponsiveContainer, Cell, Tooltip } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SMETA = {
  surging: { label: "Surging", Icon: Fire, color: "#10b981", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  growing: { label: "Growing", Icon: TrendUp, color: "#3b82f6", chip: "bg-blue-50 text-blue-700 ring-blue-200" },
  steady: { label: "Steady", Icon: Minus, color: "#71717a", chip: "bg-zinc-100 text-zinc-600 ring-zinc-200" },
  cooling: { label: "Cooling", Icon: Snowflake, color: "#f59e0b", chip: "bg-amber-50 text-amber-700 ring-amber-200" },
  emerging: { label: "Emerging", Icon: Sparkle, color: "#8b5cf6", chip: "bg-violet-50 text-violet-700 ring-violet-200" },
};

const SrcBadge = ({ source }) => {
  const map = { ycombinator: ["YC", "bg-orange-100 text-orange-700"], a16z: ["a16z", "bg-indigo-100 text-indigo-700"], accel: ["Accel", "bg-rose-100 text-rose-700"], sequoia: ["Sequoia", "bg-teal-100 text-teal-700"] };
  const [lbl, cls] = map[source] || [source, "bg-zinc-100 text-zinc-600"];
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>{lbl}</span>;
};

const CohortBars = ({ data, color, height = 44 }) => {
  if (!data || !data.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e4e4e7", padding: "3px 7px" }} formatter={(v) => [`${v} founded`, ""]} labelFormatter={(l) => l} />
        <Bar dataKey="v" radius={[2, 2, 0, 0]}>
          {data.map((d) => <Cell key={d.d} fill={Number(d.d) >= 2024 ? color : "#d4d4d8"} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const StatusChip = ({ status }) => {
  const s = SMETA[status] || SMETA.emerging; const { Icon } = s;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${s.chip}`}><Icon size={12} weight="fill" /> {s.label}</span>;
};

const CategoryPill = ({ c, active, onClick }) => {
  const s = SMETA[c.status] || SMETA.emerging;
  return (
    <button onClick={onClick} data-testid={`trending-cat-${c.key}`}
      className={`flex-shrink-0 text-left rounded-xl border p-3 min-w-[150px] transition-all ${active ? "border-violet-400 bg-violet-50/50 ring-1 ring-violet-200" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-sm text-zinc-800 truncate">{c.label}</span>
        <s.Icon size={14} weight="fill" style={{ color: s.color }} />
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: s.color }}>{c.recent_count}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400">new since 2024 · {Math.round(c.recent_share * 100)}%</div>
    </button>
  );
};

const ModelCard = ({ m, onOpen }) => {
  const s = SMETA[m.status] || SMETA.emerging;
  return (
    <button onClick={() => onOpen(m)} data-testid={`trending-model-${m.cluster_id}`}
      className="group text-left bg-white border border-zinc-200 rounded-2xl p-5 hover:border-violet-300 hover:shadow-[0_18px_44px_-22px_rgba(124,58,237,0.4)] hover:-translate-y-0.5 transition-all flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-zinc-900 text-[15px] leading-tight">{m.label}</h3>
        <StatusChip status={m.status} />
      </div>
      {m.centroid_name ? <div className="mt-1 text-xs text-zinc-500">e.g. <span className="font-medium text-zinc-700">{m.centroid_name}</span></div> : null}
      <div className="mt-3 flex items-baseline gap-4">
        <div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{m.recent_count}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">new since 2024</div>
        </div>
        <div>
          <div className="text-2xl font-bold tabular-nums text-zinc-800">{m.member_count}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">companies</div>
        </div>
        {m.exits > 0 ? (
          <div>
            <div className="text-2xl font-bold tabular-nums text-amber-600">{m.exits}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">exits</div>
          </div>
        ) : null}
      </div>
      <div className="mt-3"><CohortBars data={m.by_year} color={s.color} /></div>
      <div className="mt-2 pt-3 border-t border-zinc-100 flex items-center justify-between text-[12px] text-zinc-500">
        <span className="truncate">{(m.countries || []).slice(0, 4).join(" · ") || "—"}</span>
        <span className="inline-flex items-center gap-1 text-violet-600 font-medium group-hover:gap-1.5 transition-all">View <ArrowRight size={13} weight="bold" /></span>
      </div>
    </button>
  );
};

const ModelDrawer = ({ model, onClose, onNavigate }) => {
  const [startups, setStartups] = useState(null);
  const s = SMETA[model.status] || SMETA.emerging;
  useEffect(() => {
    let on = true;
    axios.get(`${API}/trending/corpus/startups?cluster_id=${model.cluster_id}&limit=24`).then((r) => on && setStartups(r.data || [])).catch(() => on && setStartups([]));
    return () => { on = false; };
  }, [model.cluster_id]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="trending-model-drawer">
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-zinc-100">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-zinc-900">{model.label}</h2>
              <StatusChip status={model.status} />
            </div>
            {model.centroid_name ? <p className="mt-1 text-sm text-zinc-500">A funded model — e.g. <span className="font-medium text-zinc-700">{model.centroid_name}</span> · {model.member_count} companies · {model.exits} exits</p> : null}
          </div>
          <button onClick={onClose} data-testid="trending-model-close" className="p-2 text-zinc-400 hover:text-zinc-700 rounded-lg hover:bg-zinc-100"><X size={18} /></button>
        </div>
        <div className="p-5 sm:p-6">
          <div className="rounded-xl border border-zinc-100 p-3 mb-5">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Companies founded per year (VC corpus)</div>
            <CohortBars data={model.by_year} color={s.color} height={90} />
          </div>
          <button onClick={() => { onNavigate && onNavigate("discover"); onClose(); }} data-testid="trending-model-discover"
            className="w-full mb-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white"
            style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}>
            <Trophy size={15} weight="fill" /> Find the market that's missing this — in Discover <ArrowRight size={15} weight="bold" />
          </button>
          <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Newest companies in this model</div>
          {startups === null ? <div className="text-sm text-zinc-400 py-4">Loading…</div> : startups.length === 0 ? (
            <p className="text-sm text-zinc-400">No 2023+ companies in this model.</p>
          ) : (
            <div className="space-y-2">
              {startups.map((c) => (
                <a key={c.slug || c.name} href={c.website || "#"} target="_blank" rel="noreferrer" data-testid={`trending-startup-${c.slug}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 transition-all">
                  <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500">{(c.name || "?")[0]}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2"><span className="text-sm font-medium text-zinc-800 truncate">{c.name}</span><SrcBadge source={c.source} /><span className="text-[11px] text-zinc-400">{c.founded_year}</span></span>
                    <span className="block text-[11px] text-zinc-400 truncate">{c.description}</span>
                  </span>
                  <ArrowUpRight size={15} className="text-zinc-300 flex-shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const TrendingStartups = ({ onNavigate }) => {
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [startups, setStartups] = useState([]);
  const [facets, setFacets] = useState(null);
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("momentum");
  const [openModel, setOpenModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    axios.get(`${API}/trending/corpus/categories`).then((r) => setCategories(r.data || [])).catch(() => {});
    axios.get(`${API}/trending/corpus/facets`).then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const bucketForCat = useCallback((c) => (c === "All" ? null : c), []);

  useEffect(() => {
    setLoading(true);
    setVisibleCount(12);
    const b = bucketForCat(cat);
    const mp = new URLSearchParams({ sort, limit: "48" });
    if (b) mp.set("category", b);
    Promise.all([
      axios.get(`${API}/trending/corpus/models?${mp.toString()}`),
      axios.get(`${API}/trending/corpus/startups?${b ? `category=${b}&` : ""}limit=18`),
    ]).then(([mr, sr]) => { setModels(mr.data || []); setStartups(sr.data || []); })
      .catch(() => { setModels([]); setStartups([]); })
      .finally(() => setLoading(false));
  }, [cat, sort, bucketForCat]);

  return (
    <div data-testid="trending-startups-view">
      {/* Categories */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide flex items-center gap-1.5"><Globe size={15} weight="duotone" className="text-violet-600" /> Categories — where founders are piling in</h2>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="trending-categories-row">
          {categories.map((c) => (
            <CategoryPill key={c.key} c={c} active={cat === c.key} onClick={() => setCat(cat === c.key ? "All" : c.key)} />
          ))}
        </div>
      </div>

      {/* Models toolbar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide flex items-center gap-1.5">
          <Buildings size={15} weight="duotone" className="text-violet-600" /> Trending business models {cat !== "All" ? <span className="text-zinc-400 normal-case">· {facets?.category_labels?.[cat] || cat}</span> : null}
        </h2>
        <div className="flex items-center gap-2">
          {cat !== "All" ? <button onClick={() => setCat("All")} data-testid="trending-clear-category" className="text-xs text-zinc-400 hover:text-zinc-700">clear filter</button> : null}
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="trending-models-sort"
            className="text-sm bg-white border border-zinc-200 rounded-lg px-3 py-1.5 text-zinc-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-200">
            <option value="momentum">Momentum</option>
            <option value="recent">Newest cohort</option>
            <option value="share">% recent</option>
            <option value="exits">Most exits</option>
            <option value="size">Most companies</option>
          </select>
        </div>
      </div>

      {loading && models.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">Loading models…</div>
      ) : models.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">No business models for this filter.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trending-models-grid">
          {models.slice(0, visibleCount).map((m) => <ModelCard key={m.cluster_id} m={m} onOpen={setOpenModel} />)}
        </div>
      )}
      {!loading && models.length > visibleCount && (
        <div className="flex justify-center mt-6">
          <button onClick={() => setVisibleCount((v) => v + 12)} data-testid="trending-models-load-more"
            className="px-6 py-2.5 rounded-full border border-zinc-200 text-sm font-semibold text-zinc-700 hover:border-violet-300 transition-colors">
            Show more models · {(models.length - visibleCount).toLocaleString()} more
          </button>
        </div>
      )}

      {/* Newest startups */}
      {startups.length > 0 ? (
        <div className="mt-10">
          <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide flex items-center gap-1.5 mb-4"><Sparkle size={15} weight="fill" className="text-violet-600" /> Newest funded companies {cat !== "All" ? <span className="text-zinc-400 normal-case">· {facets?.category_labels?.[cat] || cat}</span> : null}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="trending-newest-grid">
            {startups.map((c) => (
              <a key={c.slug || c.name} href={c.website || "#"} target="_blank" rel="noreferrer" data-testid={`trending-newco-${c.slug}`}
                className="flex items-start gap-3 p-3.5 rounded-xl border border-zinc-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 transition-all">
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-500">{(c.name || "?")[0]}</span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2"><span className="text-sm font-medium text-zinc-800 truncate">{c.name}</span><SrcBadge source={c.source} /><span className="text-[11px] text-zinc-400">{c.founded_year}</span></span>
                  <span className="block text-[11px] text-zinc-400 line-clamp-2">{c.description}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      ) : null}

      <p className="mt-8 text-center text-[11px] text-zinc-400 max-w-2xl mx-auto">
        "Rising" here means <span className="font-medium text-zinc-500">founding momentum</span> — the share of companies in a category/model founded since 2024, from our 4,613-company VC corpus (YC · a16z · Accel · Sequoia). Funding velocity isn't used (corpus funding data is sparse). Models need ≥5 dated companies to rank.
      </p>

      {openModel ? <ModelDrawer model={openModel} onClose={() => setOpenModel(null)} onNavigate={onNavigate} /> : null}
    </div>
  );
};

export default TrendingStartups;
